/**
 * PageEditorPage
 * Page for editing a page's sections with drag-and-drop ordering.
 */

import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { usePage } from '@/hooks/use-pages'
import { SectionManager } from '@/components/pages/SectionManager'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export function PageEditorPage() {
  const { pageId } = useParams<{ pageId: string }>()
  const navigate = useNavigate()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  // Use impersonated account when admin is impersonating
  const activeAccount = isImpersonating ? impersonatedAccount : account
  const accountId = activeAccount?.id

  // Get the base path for navigation
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Build viewer URL for preview
  const viewerBase = import.meta.env.VITE_VIEWER_URL || 'http://localhost:3001'
  const viewerUrl = viewerBase.replace(
    'localhost',
    `${activeAccount?.slug}.localhost`
  )

  // Fetch page with sections
  const { data: page, isLoading, error } = usePage(pageId)

  // Get preview URL for the page
  const previewUrl = page
    ? `${viewerUrl}${page.slug ? `/${page.slug}` : ''}`
    : viewerUrl

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    )
  }

  // Error state
  if (error || !page) {
    return (
      <div className="space-y-6">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to={`${basePath}/pages`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Pages
            </Link>
          </Button>
        </div>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold">Page Not Found</h2>
          <p className="text-muted-foreground mt-2">
            The page you're looking for doesn't exist or has been deleted.
          </p>
          <Button
            onClick={() => navigate(`${basePath}/pages`)}
            className="mt-4"
          >
            Go to Pages
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`${basePath}/pages`}>
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">Back to Pages</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{page.title}</h1>
            <p className="text-muted-foreground">
              /{page.slug || '(home)'}
            </p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            Preview
            <ChevronRight className="ml-1 h-4 w-4" />
          </a>
        </Button>
      </div>

      {/* Section Manager */}
      {accountId && (
        <SectionManager
          page={page}
          sections={page.page_sections}
          accountId={accountId}
        />
      )}
    </div>
  )
}
